import type {
  DebugAnalysis,
  DebugParseHealth,
  DebugVerdict,
  ParseHealthStatus,
} from "@/services/debugging";

/**
 * Presentation mapping for the debug-analysis verdict.
 *
 * The analyzer stopped calling every "no error found" run healthy. `unknown` (no log
 * parsed at all) and `parse_failed` (logs parsed, but parse health is not ok) are
 * "we could not determine" states. Painting them green would claim an org is fine when
 * we merely failed to read its logs; painting them red would claim a failure the org
 * never reported. Both get their own caution tone.
 */
export interface VerdictPresentation {
  /** Badge variant from the studio Badge component. */
  tone: "success" | "warm" | "destructive";
  label: string;
  detail: string;
}

const PRESENTATION: Record<DebugVerdict, VerdictPresentation> = {
  healthy: {
    tone: "success",
    label: "Healthy",
    detail: "Every requested log was parsed cleanly and none reported an error.",
  },
  failing: {
    tone: "destructive",
    label: "Failing",
    detail: "At least one log reported an error or a failed transaction status.",
  },
  unknown: {
    tone: "warm",
    label: "Undetermined",
    detail: "No logs could be analyzed, so nothing is known about this org's health.",
  },
  parse_failed: {
    tone: "warm",
    label: "Parse incomplete",
    detail:
      "Logs were analyzed but not all of them parsed, so a failure could still be hiding in what we could not read.",
  },
};

const PARSE_HEALTH_LABELS: Record<ParseHealthStatus, string> = {
  ok: "All logs parsed",
  degraded: "Some logs incomplete",
  failed: "Logs could not be parsed",
};

export function asDebugVerdict(value: unknown): DebugVerdict | null {
  return value === "healthy" || value === "failing" || value === "unknown" || value === "parse_failed"
    ? value
    : null;
}

export function verdictPresentation(verdict: DebugVerdict): VerdictPresentation {
  return PRESENTATION[verdict];
}

export function asParseHealthStatus(value: unknown): ParseHealthStatus | null {
  return value === "ok" || value === "degraded" || value === "failed" ? value : null;
}

export function parseHealthLabel(status: ParseHealthStatus): string {
  return PARSE_HEALTH_LABELS[status];
}

/** `ok` parse health needs no callout; anything else is worth showing to the user. */
export function parseHealthTone(status: ParseHealthStatus): "success" | "warm" {
  return status === "ok" ? "success" : "warm";
}

/** The verdict lives at `summary.verdict`. Returns `null` when absent or unrecognised. */
export function analysisVerdict(analysis: DebugAnalysis | null | undefined): DebugVerdict | null {
  return asDebugVerdict(analysis?.summary?.verdict);
}

/**
 * The full parse-health object. It is delivered at the top level and mirrored under
 * `diagnosis.parse_health`; `summary.parse_health` is only the status string, so it is
 * NOT a source here. Falls back to the diagnosis copy when the top-level one is absent.
 */
export function analysisParseHealth(
  analysis: DebugAnalysis | null | undefined
): DebugParseHealth | null {
  const top = analysis?.parse_health;
  if (top && asParseHealthStatus(top.status)) return top;
  const diagnosis = analysis?.diagnosis as { parse_health?: DebugParseHealth } | undefined;
  const mirrored = diagnosis?.parse_health;
  return mirrored && asParseHealthStatus(mirrored.status) ? mirrored : null;
}

/**
 * Why the analysis is incomplete, in the backend's own words. Falls back to a count
 * sentence when the backend sent a non-ok status but no reasons, so an unexplained
 * degraded state never renders as a blank panel.
 */
export function parseHealthReasons(health: DebugParseHealth | null | undefined): string[] {
  if (!health) return [];
  const reasons = Array.isArray(health.reasons) ? health.reasons.filter(Boolean) : [];
  if (reasons.length > 0) return reasons;
  if (health.status === "ok") return [];
  const parts: string[] = [];
  if (health.logs_failed) parts.push(`${health.logs_failed} log(s) could not be parsed`);
  if (health.logs_missing?.length) {
    parts.push(`${health.logs_missing.length} requested log(s) were not returned`);
  }
  return parts.length ? parts : ["The analyzer did not report why parsing was incomplete."];
}
